/**
 * BenchmarkDownloadButton
 *
 * A small floating button shown during a match analysis session.
 * Tapping it downloads the current benchmark data as a JSON file —
 * works on desktop and mobile without any localStorage flags or opt-in.
 *
 * The button is always rendered while a session is active (benchmarkService
 * has been started). It stays out of the way at the bottom-right corner and
 * only expands to show a brief stats summary when tapped on desktop (the
 * expansion is skipped on narrow screens — download fires immediately).
 */

import React, { useEffect, useRef, useState } from 'react';
import { benchmarkService } from '../utils/benchmarkService';

const POLL_MS = 3000; // refresh stats every 3 s (not 1 s — low priority)

export default function BenchmarkPanel() {
  return null;
  // eslint-disable-next-line no-unreachable
  const [stats, setStats]             = useState(() => benchmarkService.getLiveStats());
  const [hasComparison, setHasCompar] = useState(false);
  const [expanded, setExpanded]       = useState(false);
  const closeTimer                    = useRef(null);

  // Poll live stats + comparison availability while the session is running
  useEffect(() => {
    const tick = () => {
      const live = benchmarkService.getLiveStats();
      setStats(live);
      if (live.matchId) {
        setHasCompar(benchmarkService.getComparison(live.matchId) !== null);
      }
    };
    tick(); // immediate first read
    const id = setInterval(tick, POLL_MS);
    return () => clearInterval(id);
  }, []);

  // Auto-collapse the expanded view after 6 s of inactivity
  const resetCloseTimer = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setExpanded(false), 6000);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  if (!stats.isActive) return null;

  const isMobile = window.innerWidth < 600;

  const handleMainTap = () => {
    if (isMobile) {
      // On mobile just download immediately — no expansion
      benchmarkService.downloadJSON();
    } else {
      setExpanded((prev) => !prev);
      resetCloseTimer();
    }
  };

  const syncColor = stats.syncMode === 'ably' ? '#22c55e' : '#f59e0b';

  return (
    <div style={root}>
      {/* Expanded stats panel — desktop only */}
      {expanded && (
        <div style={panel}>
          <div style={panelHeader}>
            <span style={{ fontWeight: 700, fontSize: 11 }}>⏱ Sync Benchmark</span>
            <span style={{ ...badge, background: syncColor }}>{stats.syncMode?.toUpperCase()}</span>
          </div>

          <Row label="REST pushes"    value={stats.restPushCount} />
          <Row label="Ably publishes" value={stats.ablyPublishCount} />
          <Row label="REST fetches"   value={stats.restFetchCount} />
          <Row label="Events tagged"  value={stats.totalEventsTagged} />
          <Row label="Stale warns"    value={stats.staleWarnings}    warn={stats.staleWarnings > 0} />
          <Row label="Reconnects"     value={stats.reconnects}       warn={stats.reconnects > 1} />
          <Row label="Ably samples"   value={stats.ablyLatencySamples} />

          <div style={btnRow}>
            <button
              style={dlBtn}
              onClick={() => { benchmarkService.downloadJSON(); setExpanded(false); }}
              title="Download current session as JSON"
            >
              ⬇ Session JSON
            </button>
            <button
              style={{ ...dlBtn, ...cmpBtn, opacity: hasComparison ? 1 : 0.45 }}
              onClick={() => { benchmarkService.downloadComparisonJSON(); setExpanded(false); }}
              title={hasComparison
                ? 'Download Ably vs REST comparison for this match'
                : 'Run one Ably + one REST session on this match to unlock comparison'}
            >
              ⚖ Ably vs REST
            </button>
          </div>

          {/* Nudge when comparison data not yet ready */}
          {!hasComparison && (
            <div style={hint}>
              Analyse in both modes to generate comparison
            </div>
          )}
        </div>
      )}

      {/* The always-visible fab button */}
      <button
        style={{ ...fab, borderColor: syncColor }}
        onClick={handleMainTap}
        title={isMobile ? 'Download benchmark data' : 'Sync benchmark'}
      >
        {/* Sync mode dot */}
        <span style={{ ...dot, background: syncColor }} />
        {/* Icon: download on mobile, chart on desktop */}
        {isMobile ? '⬇' : '📊'}
      </button>
    </div>
  );
}

// ─── Sub-component ────────────────────────────────────────────────────────────

function Row({ label, value, warn = false }) {
  return (
    <div style={row}>
      <span style={labelStyle}>{label}</span>
      <span style={{ ...valueStyle, color: warn ? '#f87171' : '#a3e635' }}>{value ?? '—'}</span>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const root = {
  position:   'fixed',
  bottom:     16,
  right:      16,
  zIndex:     9999,
  display:    'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap:        8,
};

const fab = {
  width:          44,
  height:         44,
  borderRadius:   '50%',
  background:     'rgba(15,15,15,0.88)',
  border:         '2px solid #22c55e',
  color:          '#e2e8f0',
  fontSize:       18,
  cursor:         'pointer',
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  boxShadow:      '0 2px 12px rgba(0,0,0,0.4)',
  backdropFilter: 'blur(6px)',
  position:       'relative',
  flexShrink:     0,
};

const dot = {
  position:     'absolute',
  top:          3,
  right:        3,
  width:        8,
  height:       8,
  borderRadius: '50%',
};

const panel = {
  background:     'rgba(15,15,15,0.94)',
  color:          '#e2e8f0',
  borderRadius:   10,
  padding:        '10px 14px',
  fontFamily:     'monospace',
  fontSize:       11,
  lineHeight:     1.7,
  minWidth:       200,
  boxShadow:      '0 4px 20px rgba(0,0,0,0.5)',
  border:         '1px solid rgba(255,255,255,0.1)',
  backdropFilter: 'blur(8px)',
};

const panelHeader = {
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'space-between',
  marginBottom:   8,
  paddingBottom:  6,
  borderBottom:   '1px solid rgba(255,255,255,0.12)',
};

const badge = {
  borderRadius:    4,
  padding:         '1px 6px',
  fontSize:        9,
  fontWeight:      700,
  color:           '#000',
  letterSpacing:   '0.05em',
};

const row = {
  display:        'flex',
  justifyContent: 'space-between',
  gap:            16,
};

const labelStyle = { color: '#94a3b8' };
const valueStyle = { fontWeight: 600, color: '#a3e635' };

const btnRow = {
  display:   'flex',
  gap:       6,
  marginTop: 8,
};

const dlBtn = {
  flex:         1,
  background:   '#334155',
  color:        '#e2e8f0',
  border:       'none',
  borderRadius: 5,
  padding:      '5px 8px',
  cursor:       'pointer',
  fontSize:     10,
  letterSpacing:'0.04em',
  textAlign:    'center',
};

// Overlay style — merged on top of dlBtn for the comparison button
const cmpBtn = {
  background: '#1e3a5f',
  border:     '1px solid #3b82f6',
  color:      '#93c5fd',
};

const hint = {
  marginTop:  5,
  fontSize:   9,
  color:      '#64748b',
  textAlign:  'center',
  fontStyle:  'italic',
};
